import { QueryClientProvider } from '@tanstack/react-query';
import { CircleAlert, CircleCheck } from 'lucide-react';
import { ThemeProvider } from 'next-themes';
import React, { useEffect } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { HelmetProvider } from 'react-helmet-async';

import { isChunkLoadError } from '@/components/error-handling/helpers/is-chunk-load-error';
import { useChunkErrorRecovery } from '@/components/error-handling/hooks/use-chunk-error-recovery';
import { Button } from '@/components/ui/button';
import { AppBootSplash } from '@/app/shell/app-boot-splash';
import { GlobalDialog } from '@/app/shell/global-dialog';
import { Toaster } from '@/components/ui/sonner';
import { analytics } from '@/lib/analytics/analytics';
import { ANALYTICS_PROVIDER_TYPE } from '@/lib/analytics/types/analytics-provider-enums';

import { runWhenIdle } from '@/lib/scheduling/run-when-idle';
import { trackError } from '@/lib/monitoring/track-error';
import * as Logger from '@/lib/monitoring/logger';
import { monitoring } from '@/lib/monitoring/monitoring';
import { PwaUpdateToast } from '@/components/pwa/pwa-update-toast';
import { queryClient } from '@/lib/query/query-client';
import './provider.css';

const ReactQueryDevtools = import.meta.env.DEV
  ? React.lazy(async () => {
      const module = await import('@tanstack/react-query-devtools');
      return {
        default: module.ReactQueryDevtools,
      };
    })
  : null;

function handleAppBoundaryError(error: unknown, info: React.ErrorInfo): void {
  // Chunk-load failures are recovered via silent reload — do not file Sentry events.
  if (isChunkLoadError(error)) {
    return;
  }

  trackError(error, {
    component: 'AppProvider',
    action: 'react-error-boundary',
    metadata: {
      url: window.location.href,
      componentStack: info.componentStack,
    },
  });
}

function ErrorFallback({ error }: FallbackProps) {
  // Chunk-load failures are recovery actions, not user-facing faults — never
  // display alarming copy. The hook schedules a silent reload; render the
  // same neutral Suspense fallback in the meantime so layout stays stable.
  if (useChunkErrorRecovery(error)) {
    return <AppBootSplash />;
  }

  return (
    <div className="error-message" role="alert">
      <h2>Ooops, something went wrong ☹ </h2>
      <Button
        className="mt-4"
        onClick={() => window.location.assign(window.location.origin)}
        variant="default"
      >
        Refresh
      </Button>
    </div>
  );
}

type AppProviderProps = {
  children: React.ReactNode;
};

const HELMET_CONTEXT = {};

function AppProvider({ children }: AppProviderProps) {
  useEffect(() => {
    // Defer non-critical SDK init (Datadog browser-logs + Upstack tracking
    // pixel) until after first paint so they don't block the initial render.
    const idleTask = runWhenIdle(() => {
      monitoring.init();
      Logger.info('Initializing analytics service on app load', { service: 'app' });
      analytics.init({
        enabled: true,
        providers: [ANALYTICS_PROVIDER_TYPE.CUSTOM_PIXEL],
        debug: import.meta.env.VITE_APP_ENABLE_CONSOLE_DEBUG === 'true',
        environment: import.meta.env.VITE_APP_STAGE,
      });
    });

    return () => idleTask.cancel();
  }, []);

  return (
    <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleAppBoundaryError}>
      <HelmetProvider context={HELMET_CONTEXT}>
        <QueryClientProvider client={queryClient}>
          <ThemeProvider attribute="class" defaultTheme="light" disableTransitionOnChange enableSystem>
            {children}
            <GlobalDialog />
            <Toaster
              closeButton
              icons={{
                success: <CircleCheck className="fill-status-success stroke-white" />,
                error: <CircleAlert className="fill-destructive stroke-white" />,
              }}
            />
            <PwaUpdateToast />
            {ReactQueryDevtools ? (
              <React.Suspense fallback={null}>
                <ReactQueryDevtools initialIsOpen={false} />
              </React.Suspense>
            ) : null}
          </ThemeProvider>
        </QueryClientProvider>
      </HelmetProvider>
    </ErrorBoundary>
  );
}

export default AppProvider;
